import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import Drawer from '../../components/Drawer';
import { GraduationCap, Plus, Upload, Search, X, UserPlus, Download, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const STATUS_OPTIONS = ['active', 'inactive'];
const RELATIONS = ['Father', 'Mother', 'Guardian', 'Other'];

function StatusPill({ status }) {
  const cls = status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200';
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold uppercase border ${cls}`}>{status || 'active'}</span>;
}



/* ── Bulk upload modal ── */
function BulkUploadModal({ open, onClose, token, onSuccess }) {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleDownloadTemplate = async () => {
    try {
      await api.studentsBulkTemplateDownload(token);
      toast.show('Template downloaded', 'success');
    } catch { toast.show('Failed to download template', 'error'); }
  };

  const handleUpload = async () => {
    if (!file) { toast.show('Select a file first', 'error'); return; }
    setUploading(true);
    try {
      const r = await api.studentsBulkUpload(token, file);
      setResult(r);
      if (r.created || r.updated) onSuccess();
      toast.show(`Created: ${r.created || 0}, Updated: ${r.updated || 0}`, 'success');
    } catch (e) {
      toast.show(e?.message || 'Upload failed', 'error');
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="bulk-upload-modal">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">Bulk Upload Students</h3>
          <button onClick={() => { onClose(); setFile(null); setResult(null); }} className="p-2 rounded-xl hover:bg-gray-100 text-gray-400"><X size={20} /></button>
        </div>

        {!result ? (
          <div className="space-y-4">
            <button onClick={handleDownloadTemplate} data-testid="bulk-download-template"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors w-full">
              <Download size={16} /> Download CSV Template
            </button>
            <div>
              <label htmlFor="bulk-file" className="block text-sm font-semibold text-gray-700 mb-1.5">Upload File (.csv or .xlsx)</label>
              <input id="bulk-file" data-testid="bulk-file-input" type="file" accept=".csv,.xlsx"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
            </div>
            <button onClick={handleUpload} disabled={!file || uploading} data-testid="bulk-upload-btn"
              className="w-full h-11 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm disabled:opacity-50 hover:shadow-lg transition-all">
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bulk-upload-result">
            <div className="grid grid-cols-4 gap-3">
              {[['Created', result.created, 'text-emerald-600 bg-emerald-50'], ['Updated', result.updated, 'text-blue-600 bg-blue-50'], ['Skipped', result.skipped, 'text-amber-600 bg-amber-50'], ['Errors', (result.errors?.length || 0), 'text-red-600 bg-red-50']].map(([label, count, cls]) => (
                <div key={label} className={`rounded-xl p-3 text-center ${cls.split(' ').slice(1).join(' ')}`}>
                  <p className={`text-2xl font-extrabold tabular-nums ${cls.split(' ')[0]}`}>{count || 0}</p>
                  <p className="text-xs font-semibold text-gray-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
            {result.errors?.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-red-200 rounded-xl p-3 space-y-1">
                {result.errors.map((err, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-red-600">
                    <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                    <span>Row {err.row || i + 1}: {err.message || err.error || JSON.stringify(err)}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => { onClose(); setFile(null); setResult(null); }} data-testid="bulk-close-btn"
              className="w-full h-11 rounded-xl border border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-colors">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── New Student Drawer ── */
function NewStudentDrawer({ open, onClose, token, onSuccess }) {
  const toast = useToast();
  const [form, setForm] = useState({ name: '', phone: '', email: '', admission_no: '', grade: '', batch: '', active_status: 'active' });
  const [parents, setParents] = useState([{ name: '', phone: '', relation: 'Father', email: '' }]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      setForm({ name: '', phone: '', email: '', admission_no: '', grade: '', batch: '', active_status: 'active' });
      setParents([{ name: '', phone: '', relation: 'Father', email: '' }]);
    }
  }, [open]);

  const addParent = () => setParents(p => [...p, { name: '', phone: '', relation: 'Guardian', email: '' }]);
  const removeParent = i => setParents(p => p.filter((_, j) => j !== i));
  const setParent = (i, k, v) => setParents(p => { const c = [...p]; c[i] = { ...c[i], [k]: v }; return c; });

  const submit = async () => {
    if (!form.name.trim()) { toast.show('Name is required', 'error'); return; }
    if (!form.phone.trim()) { toast.show('Phone is required', 'error'); return; }
    setSaving(true);
    try {
      const validParents = parents.filter(p => p.name.trim() && p.phone.trim());
      await api.customerCreate(token, {
        ...form, name: form.name.trim(), phone: form.phone.trim(),
        customer_type: 'student',
        parents: validParents.length > 0 ? validParents : undefined,
      });
      toast.show('Student created', 'success');
      onSuccess();
    } catch (e) { toast.show(e?.message || 'Create failed', 'error'); } finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title="New Student" wide>
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <label className="block col-span-2"><span className="text-xs font-bold text-gray-500 uppercase">Name *</span>
            <input data-testid="ns-student-name" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" value={form.name} onChange={e => set('name', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Phone *</span>
            <input data-testid="ns-student-phone" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" value={form.phone} onChange={e => set('phone', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Email</span>
            <input data-testid="ns-student-email" type="email" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" value={form.email} onChange={e => set('email', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Admission No</span>
            <input data-testid="ns-student-admission" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" value={form.admission_no} onChange={e => set('admission_no', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Grade</span>
            <input data-testid="ns-student-grade" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" value={form.grade} onChange={e => set('grade', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Batch</span>
            <input data-testid="ns-student-batch" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" value={form.batch} onChange={e => set('batch', e.target.value)} /></label>
          <label className="block"><span className="text-xs font-bold text-gray-500 uppercase">Status</span>
            <select data-testid="ns-student-status" className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none bg-white" value={form.active_status} onChange={e => set('active_status', e.target.value)}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
            </select></label>
        </div>

        {/* Parents repeater */}
        <div data-testid="ns-parents-section">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-gray-500 uppercase">Parents / Guardians</span>
            <button onClick={addParent} data-testid="ns-add-parent" className="flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"><Plus size={14} /> Add</button>
          </div>
          {parents.map((p, i) => (
            <div key={i} data-testid={`ns-parent-${i}`} className="grid grid-cols-2 gap-3 mb-3 p-3 bg-gray-50 rounded-xl relative">
              {parents.length > 1 && <button onClick={() => removeParent(i)} className="absolute top-2 right-2 p-1 rounded hover:bg-gray-200 text-gray-400"><X size={14} /></button>}
              <input placeholder="Name *" data-testid={`ns-parent-name-${i}`} className="px-3 py-2 rounded-lg border border-gray-200 text-sm" value={p.name} onChange={e => setParent(i, 'name', e.target.value)} />
              <input placeholder="Phone *" data-testid={`ns-parent-phone-${i}`} className="px-3 py-2 rounded-lg border border-gray-200 text-sm" value={p.phone} onChange={e => setParent(i, 'phone', e.target.value)} />
              <select data-testid={`ns-parent-relation-${i}`} className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white" value={p.relation} onChange={e => setParent(i, 'relation', e.target.value)}>
                {RELATIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <input placeholder="Email" data-testid={`ns-parent-email-${i}`} type="email" className="px-3 py-2 rounded-lg border border-gray-200 text-sm" value={p.email} onChange={e => setParent(i, 'email', e.target.value)} />
            </div>
          ))}
        </div>

        <button onClick={submit} disabled={saving} data-testid="ns-student-submit"
          className="w-full h-12 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold text-sm disabled:opacity-50 hover:shadow-lg transition-all">
          {saving ? 'Creating...' : 'Create Student'}
        </button>
      </div>
    </Drawer>
  );
}

/* ── Main Students page ── */
export default function Students() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 25;

  const [newOpen, setNewOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await api.customersList(token, { customer_type: 'student', q: search || undefined, active_status: statusFilter || undefined, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE });
      const allItems = r.items || [];
      // Client-side filter by grade/batch if backend doesn't support it
      let filtered = allItems;
      if (gradeFilter) filtered = filtered.filter(s => s.grade === gradeFilter);
      if (batchFilter) filtered = filtered.filter(s => s.batch === batchFilter);
      setItems(filtered);
      setTotal(r.total ?? allItems.length);
    } catch (e) {
      if (e?.status === 403) return; // handled by locked panel
      setItems([]);
    } finally { setLoading(false); }
  }, [token, search, statusFilter, gradeFilter, batchFilter, page]);

  useEffect(() => { load(); }, [load]);

  // Extract unique grades/batches for filter chips
  const grades = useMemo(() => [...new Set(items.map(s => s.grade).filter(Boolean))], [items]);
  const batches = useMemo(() => [...new Set(items.map(s => s.batch).filter(Boolean))], [items]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="students-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 flex items-center gap-3"><GraduationCap size={28} className="text-indigo-600" /> Students</h1>
          <p className="text-gray-500 text-sm mt-1">{total} student{total !== 1 ? 's' : ''} total</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setBulkOpen(true)} data-testid="bulk-upload-open-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
            <Upload size={16} /> Bulk Upload
          </button>
          <button onClick={() => setNewOpen(true)} data-testid="new-student-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-bold shadow-lg hover:shadow-xl hover:brightness-110 transition-all">
            <Plus size={16} /> New Student
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="student-search" placeholder="Search name, admission no, parent phone..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none" />
        </div>
        <select data-testid="student-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 font-semibold">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        {grades.length > 0 && (
          <select data-testid="student-grade-filter" value={gradeFilter} onChange={e => { setGradeFilter(e.target.value); setPage(1); }}
            className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 font-semibold">
            <option value="">All Grades</option>
            {grades.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        )}
        {batches.length > 0 && (
          <select data-testid="student-batch-filter" value={batchFilter} onChange={e => { setBatchFilter(e.target.value); setPage(1); }}
            className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white text-gray-700 font-semibold">
            <option value="">All Batches</option>
            {batches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="students-empty">
          <div className="w-20 h-20 rounded-2xl bg-indigo-50 flex items-center justify-center mb-5"><GraduationCap size={36} className="text-indigo-400" /></div>
          <h3 className="text-gray-900 font-bold text-lg mb-2">No students yet</h3>
          <p className="text-gray-500 text-sm mb-5 max-w-sm">Add your first student or use bulk upload to import from a CSV file.</p>
          <div className="flex gap-3">
            <button onClick={() => setBulkOpen(true)} className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50"><Upload size={14} className="inline mr-1.5" />Bulk Upload</button>
            <button onClick={() => setNewOpen(true)} className="px-5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700"><UserPlus size={14} className="inline mr-1.5" />Add Student</button>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden" data-testid="students-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Admission No</th>
                  <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Grade</th>
                  <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Batch</th>
                  <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Parents</th>
                  <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Phone</th>
                  <th className="text-left px-4 py-3 text-gray-500 text-xs font-bold uppercase">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map(s => (
                  <tr key={s.id} data-testid={`student-row-${s.id}`} onClick={() => navigate(`/school/students/${s.id}`)}
                    className="border-b border-gray-100 last:border-0 hover:bg-indigo-50/40 cursor-pointer transition-colors h-[52px]">
                    <td className="px-5 py-3 text-gray-900 font-semibold">{s.name}</td>
                    <td className="px-4 py-3 text-gray-600 tabular-nums">{s.admission_no || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{s.grade || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{s.batch || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{(s.parents || []).map(p => p.name).join(', ') || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 tabular-nums">{s.phone || '—'}</td>
                    <td className="px-4 py-3"><StatusPill status={s.active_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-gray-500 text-xs">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft size={14} className="inline" /> Prev</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-40 hover:bg-gray-50">Next <ChevronRight size={14} className="inline" /></button>
            </div>
          </div>
        </>
      )}

      <NewStudentDrawer open={newOpen} onClose={() => setNewOpen(false)} token={token} onSuccess={() => { setNewOpen(false); load(); }} />
      <BulkUploadModal open={bulkOpen} onClose={() => setBulkOpen(false)} token={token} onSuccess={load} />
    </div>
  );
}
